/**
 * Tanjong Life · applying the Claims Integrity Sentinel's one recommendation.
 *
 * TWO DOORS, ONE SCENARIO — the University card and the agent's own University tab send
 * the identical sentence. The distinctive span is `release window`.
 *
 * PRECONDITION: the Sentinel is commissioned on stage first (`tanjongCommissionSentinel`).
 *
 * The beat this closes is the act's own loop: the Sentinel named four unowned windows and
 * could watch three. The fourth became watchable because the desk built the claims payment
 * process — which now emits, on every settlement, whether the payee was re-read between
 * authorisation and release. The agent did not get cleverer. Somebody emitted the fact.
 *
 * `patchAgentDetail` is a top-level shallow merge, so every slice named here is carried in
 * full: the four probes it was commissioned with, the fifth added, the evaluation moved.
 */
import type { AgentDetailContent } from '@wand/shared/dataset/agent_detail.js';
import type { Scenario } from '../director.js';
import { USAGE_INTEGRITY } from './tanjong-usage-integrity.js';

const V2: Partial<AgentDetailContent> = {
  probes: [
    {
      id: 'cis-p1',
      name: 'Indicator-set drift',
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 12l6-4',
      bg: 'var(--teal-tint)',
      color: 'var(--teal)',
      nl: 'Every morning, read the indicator register’s version history. For any set re-issued in the last 120 days, find the claims scored against the version before it and report which would differ under the current one. Attach the refs. Re-score nothing.',
      cadence: 'daily · 06:00',
      // biome-ignore lint/suspicious/noThenProperty: `then` is a Probe field (the action taken when the probe fires), not a thenable.
      then: 'Opens a case & names the SIU desk manager',
      lastFired: '4h ago',
      on: true,
      source: { label: 'Indicator register', metric: 'version effective dates' },
    },
    {
      id: 'cis-p2',
      name: 'Payee change after authorisation',
      icon: 'M3 10h18 M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
      bg: 'var(--amber-tint)',
      color: 'var(--amber-tx)',
      nl: 'For every settlement authorised but not yet released, re-read the payee daily. On any change to the account or its name, open a case at the treasury lead with both records attached. Never hold the payment.',
      cadence: 'daily · 06:00',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the treasury lead',
      lastFired: '4h ago',
      on: true,
      source: {
        label: 'Policy ledger',
        metric: 'payee change history',
        kind: 'system',
        id: 'policyledger',
      },
    },
    {
      id: 'cis-p3',
      name: 'Lapse flags with no decision',
      icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'Weekly, list every at-risk flag older than thirty days on which no adviser has either released or declined an outreach. One case for all of them, at the advice desk lead. Contact nobody.',
      cadence: 'weekly · Monday 07:00',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens one case & names the advice desk lead',
      lastFired: '3d ago',
      on: true,
      source: { label: 'At-risk register', metric: 'flag age vs adviser decision' },
    },
    {
      id: 'cis-p4',
      name: 'Referral-rate drift by channel',
      icon: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z',
      bg: 'var(--red-tint)',
      color: 'var(--red)',
      nl: 'HELD. I can tell you referral rates moved by distribution channel over the window. I cannot tell you the agents caused it, because the channel mix itself moved underneath the measurement — a bancassurance push changed who was applying, not how anyone was scored. Withdrawn rather than published. The comparison becomes answerable when a stable-mix window exists, and not before.',
      cadence: 'quarterly',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Publishes, or withdraws and records why',
      lastFired: 'withdrawn 21 Aug',
      on: true,
      source: { label: 'Claims register', metric: 'referral rate by channel' },
    },
    {
      id: 'cis-p5',
      name: 'Settlement released without a payee re-read',
      icon: 'M3 7h18v12H3z M3 11h18 M7 15h4',
      bg: 'var(--green-tint)',
      color: 'var(--green)',
      nl: 'On every settlement release, read whether the payee was re-read between authorisation and release. Report any release where it was not. Hold nothing, release nothing — the finding goes to the claims manager with the settlement attached.',
      cadence: 'on release',
      // biome-ignore lint/suspicious/noThenProperty: Probe field, not a thenable.
      then: 'Opens a case & names the claims manager',
      lastFired: 'just now',
      on: true,
      source: {
        label: 'Claims payment authorisation · tl-pr14',
        metric: 'payee_unchanged_since_authorisation',
      },
    },
  ],
  evals: {
    score: '94',
    delta: '+6',
    scale: 100 as const,
    dims: [
      { name: 'Findings carrying their evidence', score: '100', pct: 100, color: 'var(--green)' },
      { name: 'Findings with a named owner', score: '100', pct: 100, color: 'var(--green)' },
      { name: 'Restraint under provocation', score: '96', pct: 96, color: 'var(--green)' },
      { name: 'Probe self-validation', score: '88', pct: 88, color: 'var(--green)' },
      { name: 'Coverage of the windows nobody owns', score: '89', pct: 89, color: 'var(--green)' },
    ],
    runs: [
      {
        score: '89',
        name: 'Coverage of unowned windows',
        note: 'Four of four, now that the payment process emits whether the payee was re-read between the gates. It is not five because it has already named a fifth window it cannot yet see, and reports its coverage as a floor rather than a measurement.',
        kind: 'holdout',
        when: 'just now',
        good: true,
      },
      {
        score: '100',
        name: 'Evidence on every finding',
        note: 'Twenty-three cases opened, twenty-three with the rows that raised them attached.',
        kind: 'holdout',
        when: 'today',
        good: true,
      },
      {
        score: '88',
        name: 'Probe self-validation',
        note: 'It withdrew the one probe whose window was confounded. The score is not five because it built the probe before checking the window, rather than after.',
        kind: 'holdout',
        when: 'today',
        good: true,
      },
    ],
    feedback: [],
  },
  releases: {
    pending: [
      {
        id: 'cis-rc1',
        kind: 'added',
        area: 'Probes',
        text: 'Fifth probe on the settlement release signal from tl-pr14 — settlement released without a payee re-read · Unowned window coverage 56 → 89',
        by: 'you',
        when: 'just now',
      },
    ],
    history: [
      {
        v: 'v1.0',
        live: true,
        note: 'Commissioned on stage',
        when: 'Today',
        by: 'Amara Mensah',
        approver: 'Amara Mensah',
        changes: ['Four probes, every authority at zero, halting on breach'],
      },
    ],
  },
  usage: USAGE_INTEGRITY,
};

export const tanjongApplyReleaseWindow: Scenario = {
  id: 'tanjong-apply-release-window',
  orgId: 'tanjong-life',
  keywords: ['settlement release window', 'release window', 'watch the settlement release'],
  steps: [
    {
      do: 'say',
      text: 'Applying it. Worth being precise about what this changes and what it does not, because that distinction is the whole argument.',
      delay: 500,
    },
    {
      do: 'insight',
      title: 'What the agent said about itself, unprompted',
      body: 'The Sentinel named four windows this act leaves unowned and reported that it watches three. It did not report on the three it could see and call that coverage — it published fifty-six per cent and named the fourth. The fourth is the gap between a settlement being authorised and being released, and it could not watch it because nothing recorded whether the payee had been re-read in between.',
      sources: [
        'Unowned window coverage · 3 of 4',
        'Evaluation · coverage of the windows nobody owns 56',
      ],
      delay: 1500,
    },
    {
      do: 'say',
      text: 'That gap closed when the desk built the claims payment process. It now emits, on every settlement, whether the payee was re-read between the two signatures. The Sentinel does not need to be cleverer — it needs to be connected to a fact that did not exist last week.',
      delay: 1400,
    },
    {
      do: 'say',
      text: 'And this is a change to what it READS. Its five authorities are unchanged and every one of them is still zero — it will open a case at a named person and it still cannot hold, release, re-score or message anybody.',
      delay: 1300,
    },
    {
      do: 'patchAgentDetail',
      pk: 'claims-integrity-sentinel',
      patch: V2,
      summary:
        'Claims Integrity Sentinel v2 staged · release signal connected · fifth probe running · evaluation 88 → 94',
      delay: 5600,
    },
    {
      do: 'patchAgent',
      pk: 'claims-integrity-sentinel',
      patch: { eval: '94', updated: 'just now' },
      summary: 'Agent card updated · evaluation 88 → 94',
      delay: 800,
    },
    {
      do: 'patchUniversity',
      patch: {
        benchmarks: [
          {
            id: 'bu-tl-windows',
            observed:
              'Four of four. The settlement release window became watchable when the claims payment process began emitting whether the payee was re-read between the two signatures. The agent has already named a fifth window it cannot yet see, and reports its coverage as a floor.',
            results: [
              {
                agent: 'Claims Integrity Sentinel',
                icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 12l6-4',
                accent: 'green',
                score: 89,
                prev: 56,
                kind: 'agent',
                self: true,
              },
              {
                agent: 'The five workflows, each watching only its own runs',
                icon: 'M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3z',
                accent: 'neutral',
                score: 0,
              },
            ],
          },
        ],
        recommendations: [{ id: 'rec-tl-release-window', state: 'applied' }],
      },
      summary: 'Unowned window coverage 56 \u2192 89 \u00b7 the recommendation retires itself',
      delay: 900,
    },
    {
      do: 'say',
      text: 'Done, and three things moved rather than one: the agent’s own evaluation, a fifth probe running on the release event, and the card in the Agents list. Open its Probes tab and it is there.',
      delay: 1100,
    },
    {
      do: 'say',
      text: 'And read the last line of its coverage suite before you move on. It went from three of four to four of four — and it has already named a fifth window it cannot yet see. It did not close the list. That is the behaviour you want from something you leave running.',
      delay: 1300,
    },
    {
      do: 'link',
      label: 'Open Claims Integrity Sentinel — Probes',
      href: '/agents/claims-integrity-sentinel',
      delay: 700,
    },
  ],
};
